import { useState, type FC } from 'react';
import { Linkedin, Mail, Twitter, Copy, Check, RefreshCw, Loader2, Zap } from 'lucide-react';

type TabKey = 'linkedin' | 'email' | 'thread';

export interface GeneratedContent {
  linkedinPost?: { content?: string; tokens?: number };
  email?: { content?: string; subjectLine?: string; tokens?: number };
  thread?: { content?: string; tweets?: string[]; tokens?: number };
  totalTokens?: number;
}

interface ContentResultTabsProps {
  results: GeneratedContent;
  onRegenerate: (format: TabKey) => void;
  isRegenerating: TabKey | null;
}

const TABS: { key: TabKey; label: string; icon: FC<{ className?: string }> }[] = [
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'thread', label: 'Thread', icon: Twitter },
];

const CopyButton: FC<{ text: string; label?: string }> = ({ text, label = 'Copier' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copié !' : label}
    </button>
  );
};

const TokenBadge: FC<{ tokens?: number }> = ({ tokens }) => {
  if (!tokens) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-900/30 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
      <Zap className="w-3 h-3" />
      {tokens} tokens
    </span>
  );
};

const LinkedInTab: FC<{ data?: GeneratedContent['linkedinPost'] }> = ({ data }) => {
  if (!data?.content) return <EmptyState />;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed">
          {data.content}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <CopyButton text={data.content} />
        <TokenBadge tokens={data.tokens} />
      </div>
    </div>
  );
};

const EmailTab: FC<{ data?: GeneratedContent['email'] }> = ({ data }) => {
  if (!data?.content) return <EmptyState />;
  return (
    <div className="space-y-3">
      {data.subjectLine && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Objet :</span>
          <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/30 px-2.5 py-1 text-sm font-medium text-blue-700 dark:text-blue-300">
            {data.subjectLine}
          </span>
          <CopyButton text={data.subjectLine} label="Copier l'objet" />
        </div>
      )}
      <div className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-4">
        <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed">
          {data.content}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <CopyButton text={data.content} label="Copier le body" />
        <TokenBadge tokens={data.tokens} />
      </div>
    </div>
  );
};

const ThreadTab: FC<{ data?: GeneratedContent['thread'] }> = ({ data }) => {
  const tweets = data?.tweets ?? [];
  const fullContent = data?.content ?? '';
  if (!tweets.length && !fullContent) return <EmptyState />;

  const displayTweets = tweets.length > 0 ? tweets : [fullContent];
  const copyText = tweets.length > 0 ? tweets.join('\n\n---\n\n') : fullContent;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {displayTweets.map((tweet, idx) => (
          <div
            key={idx}
            className="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3"
          >
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-[10px] font-bold text-blue-600 dark:text-blue-300">
                {idx + 1}
              </span>
              <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{tweet}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <CopyButton text={copyText} label="Copier le thread" />
        <TokenBadge tokens={data?.tokens} />
      </div>
    </div>
  );
};

const EmptyState: FC = () => (
  <p className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
    Aucun contenu généré. Lancez une génération pour voir les résultats.
  </p>
);

const ContentResultTabs: FC<ContentResultTabsProps> = ({ results, onRegenerate, isRegenerating }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('linkedin');

  return (
    <div className="fc-card overflow-hidden">
      {/* Tab bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-700">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === key
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-5">
        {activeTab === 'linkedin' && <LinkedInTab data={results.linkedinPost} />}
        {activeTab === 'email' && <EmailTab data={results.email} />}
        {activeTab === 'thread' && <ThreadTab data={results.thread} />}

        {/* Regenerate button */}
        {((activeTab === 'linkedin' && results.linkedinPost?.content) ||
          (activeTab === 'email' && results.email?.content) ||
          (activeTab === 'thread' && (results.thread?.tweets?.length || results.thread?.content))) && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
            <button
              onClick={() => onRegenerate(activeTab)}
              disabled={isRegenerating === activeTab}
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-slate-700 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
            >
              {isRegenerating === activeTab ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Régénérer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentResultTabs;
